import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Syringe, AlertTriangle, ChevronDown, Info } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { SaveResultButton } from '../components/tools/SaveResultButton';
import { ToolSEOWrapper } from '../components/tools/ToolSEOWrapper';
import { EmailResultsGate } from '../components/tools/EmailResultsGate';

const PEPTIDES = [
  {
    id: 'bpc157', name: 'BPC-157',
    category: 'Recovery',
    doseRange: { min: 200, max: 500, unit: 'mcg' },
    frequency: '1-2x daily', route: 'Subcutaneous (near injury site)',
    cycleWeeks: { min: 4, max: 12 },
    notes: 'Best taken close to the site of injury. Can split AM/PM doses. Research peptide with extensive gut healing and tissue repair data.',
    goals: ['Recovery', 'Gut Health', 'Tendon Repair'],
    halfLife: '~4 hours',
  },
  {
    id: 'tb500', name: 'TB-500 (Thymosin Beta-4)',
    category: 'Recovery',
    doseRange: { min: 2000, max: 2500, unit: 'mcg' },
    frequency: 'Loading: 2x/week (4-6 wks) then 1x/week',
    route: 'Subcutaneous',
    cycleWeeks: { min: 8, max: 12 },
    notes: 'Systemic recovery peptide. Works well stacked with BPC-157 for accelerated healing. Can inject anywhere subcutaneously.',
    goals: ['Recovery', 'Flexibility', 'Tissue Repair'],
    halfLife: '~24 hours',
  },
  {
    id: 'cjc_ipa', name: 'CJC-1295 / Ipamorelin',
    category: 'Growth Hormone',
    doseRange: { min: 100, max: 300, unit: 'mcg each' },
    frequency: '1-3x daily (before bed optimal)',
    route: 'Subcutaneous',
    cycleWeeks: { min: 8, max: 16 },
    notes: 'Combined GH-releasing stack. Take on empty stomach. Avoid eating 30 min before/after. Before bed dose is most effective for GH pulse.',
    goals: ['Muscle Growth', 'Recovery', 'Anti-Aging', 'Fat Loss'],
    halfLife: 'CJC: ~30 min | Ipa: ~2 hours',
  },
  {
    id: 'semaglutide', name: 'Semaglutide',
    category: 'Weight Management',
    doseRange: { min: 250, max: 2400, unit: 'mcg' },
    frequency: '1x/week',
    route: 'Subcutaneous',
    cycleWeeks: { min: 16, max: 52 },
    notes: 'Start at 0.25mg/week, titrate up every 4 weeks: 0.25 → 0.5 → 1.0 → 1.7 → 2.4mg. Do not jump doses. Inject same day each week.',
    goals: ['Fat Loss', 'Appetite Control'],
    halfLife: '~7 days',
    titration: [
      { week: '1-4', dose: 250, label: '0.25 mg' },
      { week: '5-8', dose: 500, label: '0.5 mg' },
      { week: '9-12', dose: 1000, label: '1.0 mg' },
      { week: '13-16', dose: 1700, label: '1.7 mg' },
      { week: '17+', dose: 2400, label: '2.4 mg' },
    ],
  },
  {
    id: 'aod9604', name: 'AOD-9604',
    category: 'Fat Loss',
    doseRange: { min: 250, max: 300, unit: 'mcg' },
    frequency: '1x daily (morning, fasted)',
    route: 'Subcutaneous (abdominal)',
    cycleWeeks: { min: 8, max: 12 },
    notes: 'Fragment of HGH (176-191). Take fasted in the morning for optimal fat metabolism. No impact on blood sugar or growth.',
    goals: ['Fat Loss'],
    halfLife: '~30 minutes',
  },
  {
    id: 'ghkcu', name: 'GHK-Cu',
    category: 'Healing',
    doseRange: { min: 200, max: 600, unit: 'mcg' },
    frequency: '1-2x daily',
    route: 'Subcutaneous or Topical',
    cycleWeeks: { min: 4, max: 8 },
    notes: 'Copper peptide for skin, hair, and wound healing. Available as injection or topical cream. Research shows collagen stimulation and anti-inflammatory effects.',
    goals: ['Skin Health', 'Hair Growth', 'Wound Healing'],
    halfLife: '~4 hours',
  },
  {
    id: 'pt141', name: 'PT-141 (Bremelanotide)',
    category: 'Wellness',
    doseRange: { min: 1000, max: 2000, unit: 'mcg' },
    frequency: 'As needed (max 2x/week)',
    route: 'Subcutaneous',
    cycleWeeks: { min: 0, max: 0 },
    notes: 'Take 45-60 minutes before activity. Do not exceed 2 doses per week. May cause nausea at higher doses — start at 1mg.',
    goals: ['Wellness'],
    halfLife: '~2-4 hours',
    asNeeded: true,
  },
];

const EXPERIENCE = [
  { value: 'beginner', label: 'Beginner', factor: 0.6 },
  { value: 'intermediate', label: 'Intermediate', factor: 0.8 },
  { value: 'advanced', label: 'Advanced', factor: 1.0 },
];

const Select = ({ label, value, options, onChange, testId, renderLabel }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => (o.id || o.value) === value);
  return (
    <div className="relative" data-testid={testId}>
      <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">{label}</label>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm hover:border-[#E63946]/50 transition-colors">
        <span>{renderLabel ? renderLabel(selected) : (selected?.name || selected?.label || 'Select...')}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 w-full mt-1 bg-zinc-800 border border-zinc-700 shadow-xl max-h-64 overflow-y-auto">
          {options.map(o => (
            <button key={o.id || o.value} type="button"
              onClick={() => { onChange(o.id || o.value); setOpen(false); }}
              className={`w-full text-left px-4 py-3 text-sm ${(o.id || o.value) === value ? 'bg-[#E63946]/20 text-white' : 'text-zinc-300 hover:bg-zinc-700'}`}>
              {renderLabel ? renderLabel(o) : (o.name || o.label)}
              {o.category && <span className="ml-2 text-zinc-500 text-xs">({o.category})</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

function formatDose(mcg) {
  if (mcg >= 1000) return `${(mcg / 1000).toFixed(mcg % 1000 === 0 ? 0 : 1)} mg`;
  return `${Math.round(mcg)} mcg`;
}

export default function PeptideDosingCalculatorPage() {
  const [peptideId, setPeptideId] = useState('bpc157');
  const [weight, setWeight] = useState('');
  const [unit, setUnit] = useState('imperial');
  const [experience, setExperience] = useState('beginner');
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const { saveResult } = useResults();

  const peptide = PEPTIDES.find(p => p.id === peptideId);

  const calculate = () => {
    if (!weight || !peptide) return;
    const rawWeight = parseFloat(weight);
    if (rawWeight <= 0 || isNaN(rawWeight)) return;
    const exp = EXPERIENCE.find(e => e.value === experience);
    const wKg = unit === 'imperial' ? rawWeight * 0.453592 : rawWeight;

    const range = peptide.doseRange;
    const baseDose = range.min + (range.max - range.min) * exp.factor;
    const recommendedDose = Math.round(baseDose);

    const cycleWeeks = peptide.asNeeded ? 0
      // eslint-disable-next-line no-nested-ternary -- readable tier picker
      : Math.round(peptide.cycleWeeks.min + (peptide.cycleWeeks.max - peptide.cycleWeeks.min) * (experience === 'beginner' ? 0 : experience === 'intermediate' ? 0.5 : 1));

    let dosesPerWeek = 7;
    if (peptide.id === 'tb500') dosesPerWeek = experience === 'beginner' ? 2 : 2;
    else if (peptide.id === 'semaglutide') dosesPerWeek = 1;
    else if (peptide.id === 'mk677') dosesPerWeek = 7;
    else if (peptide.id === 'pt141') dosesPerWeek = 1;
    // eslint-disable-next-line no-nested-ternary -- readable tier picker
    else if (peptide.id === 'cjc_ipa') dosesPerWeek = experience === 'beginner' ? 7 : experience === 'intermediate' ? 14 : 21;
    else dosesPerWeek = peptide.frequency.includes('2x') ? 14 : 7;

    const weeklyTotal = recommendedDose * dosesPerWeek;
    const cycleTotal = peptide.asNeeded ? 0 : weeklyTotal * cycleWeeks;

    setResult({
      peptide,
      dose: recommendedDose,
      dosesPerWeek,
      weeklyTotal,
      cycleWeeks,
      cycleTotal,
      weightKg: wKg,
      dosePerKg: (recommendedDose / wKg).toFixed(1),
    });
    setSaved(false);
  };

  return (
    <ToolSEOWrapper toolKey="peptide-dosing">
    <main className="pt-20 min-h-screen" data-testid="peptide-dosing-page">
      <section className="container-ninja section-spacing">
        <Link to="/tools" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /><span className="text-sm uppercase tracking-wider font-heading">Back to Tools</span>
        </Link>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-[#E63946] text-white mb-4">Calculator</span>
            <h1 className="text-4xl md:text-5xl text-white font-heading mb-3" data-testid="peptide-dosing-title">
              Peptide Dosing Calculator
            </h1>
            <p className="text-zinc-400">Get recommended dosing protocols based on peptide type, body weight, and experience level</p>
          </div>

          <div className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8">
            <Select
              label="Peptide"
              value={peptideId}
              options={PEPTIDES}
              onChange={setPeptideId}
              testId="peptide-select"
              renderLabel={o => o ? `${o.name}` : 'Select...'}
            />

            {peptide && (
              <div className="mt-3 mb-4 flex flex-wrap gap-2">
                {peptide.goals.map(g => (
                  <span key={g} className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-zinc-800 text-zinc-400 border border-zinc-700">{g}</span>
                ))}
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#E63946]/10 text-[#E63946] border border-[#E63946]/20">
                  {peptide.route}
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 mt-4">
              <div>
                <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">
                  {unit === 'imperial' ? 'Weight (lbs)' : 'Weight (kg)'}
                </label>
                <input type="number" value={weight} onChange={e => setWeight(e.target.value)} placeholder="Body weight"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors"
                  data-testid="weight-input" />
              </div>
              <Select label="Units" value={unit}
                options={[{ value: 'imperial', label: 'Imperial (lbs)' }, { value: 'metric', label: 'Metric (kg)' }]}
                onChange={setUnit} testId="unit-select"
                renderLabel={o => o?.label || 'Select...'} />
              <Select label="Experience" value={experience} options={EXPERIENCE}
                onChange={setExperience} testId="experience-select"
                renderLabel={o => o?.label || 'Select...'} />
            </div>

            <button onClick={calculate} disabled={!weight}
              className="w-full py-4 bg-gradient-to-r from-[#E63946] to-[#ff6b35] text-white font-heading text-lg uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="calculate-dose-btn">
              Calculate Dose
            </button>
          </div>

          {result && (
            <div className="mt-8 animate-entrance" data-testid="peptide-dose-results">
              <EmailResultsGate
                toolId="peptide-dosing"
                toolName="Peptide Dosing Protocol"
                ctaLabel="Email me my full protocol PDF"
                resultPayload={{
                  peptide: result.peptide.name,
                  dose: result.dose,
                  weeklyTotal: result.weeklyTotal,
                  cycleWeeks: result.cycleWeeks,
                  dosePerKg: result.dosePerKg,
                }}
              >
              {/* Main dose card */}
              <div className="bg-zinc-900/80 border border-[#E63946]/30 p-6 md:p-8 mb-4">
                <div className="flex items-center gap-3 mb-6">
                  <Syringe className="w-5 h-5 text-[#E63946]" />
                  <h3 className="text-white text-lg font-heading">{result.peptide.name} Protocol</h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-zinc-800 p-4 border border-zinc-700 text-center">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Dose / Injection</p>
                    <p className="text-2xl font-heading text-white" data-testid="dose-per-injection">{formatDose(result.dose)}</p>
                  </div>
                  <div className="bg-zinc-800 p-4 border border-zinc-700 text-center">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Frequency</p>
                    <p className="text-lg font-heading text-[#00F0FF]">{result.peptide.frequency.split('(')[0].trim()}</p>
                  </div>
                  <div className="bg-zinc-800 p-4 border border-zinc-700 text-center">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Weekly Total</p>
                    <p className="text-2xl font-heading text-white">{formatDose(result.weeklyTotal)}</p>
                  </div>
                  <div className="bg-zinc-800 p-4 border border-zinc-700 text-center">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Cycle Length</p>
                    <p className="text-2xl font-heading text-[#e8ff00]">
                      {result.peptide.asNeeded ? 'PRN' : `${result.cycleWeeks} wks`}
                    </p>
                  </div>
                </div>

                {/* Dose per kg */}
                <div className="flex items-center gap-4 text-sm text-zinc-400 mb-4">
                  <span>Dose/kg: <strong className="text-white">{result.dosePerKg} mcg/kg</strong></span>
                  <span>Half-life: <strong className="text-white">{result.peptide.halfLife}</strong></span>
                  {result.cycleTotal > 0 && (
                    <span>Total for cycle: <strong className="text-white">{formatDose(result.cycleTotal)}</strong></span>
                  )}
                </div>

                {/* Titration schedule for semaglutide */}
                {result.peptide.titration && (
                  <div className="mt-4 border-t border-zinc-800 pt-4">
                    <p className="text-zinc-400 text-xs uppercase tracking-wider mb-3 font-heading">Titration Schedule</p>
                    <div className="grid grid-cols-5 gap-2">
                      {result.peptide.titration.map((t, i) => (
                        <div key={t.label} className={`text-center p-2 border ${i === 0 ? 'border-[#E63946]/40 bg-[#E63946]/10' : 'border-zinc-700 bg-zinc-800'}`}>
                          <p className="text-[10px] text-zinc-500 uppercase">Wk {t.week}</p>
                          <p className="text-white text-sm font-heading">{t.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="bg-zinc-900/80 border border-zinc-800 p-6 mb-4">
                <div className="flex items-start gap-3">
                  <Info className="w-4 h-4 text-[#00F0FF] mt-0.5 flex-shrink-0" />
                  <p className="text-zinc-400 text-sm leading-relaxed">{result.peptide.notes}</p>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="bg-[#f59e0b]/5 border border-[#f59e0b]/20 p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-[#f59e0b] mt-0.5 flex-shrink-0" />
                  <p className="text-zinc-500 text-xs leading-relaxed">
                    <strong className="text-[#f59e0b]">Disclaimer:</strong> This calculator provides general dosing guidelines for educational purposes only. Peptide dosing should be supervised by a licensed healthcare provider. Individual responses vary. Always start with the lowest effective dose.
                  </p>
                </div>
              </div>

              <SaveResultButton
                saved={saved}
                onSave={() => {
                  saveResult('peptideDose', {
                    value: result.dose,
                    label: `${result.peptide.name}: ${formatDose(result.dose)} ${result.peptide.frequency.split('(')[0].trim()}`,
                    peptide: result.peptide.name,
                    doseFormatted: formatDose(result.dose),
                    frequency: result.peptide.frequency,
                    cycleWeeks: result.cycleWeeks,
                    weeklyTotal: formatDose(result.weeklyTotal),
                  });
                  setSaved(true);
                }}
              />
              </EmailResultsGate>
            </div>
          )}
        </div>
      </section>
    </main>
    </ToolSEOWrapper>
  );
}
